import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Breadcrumbs,
  Badge,
  alpha,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PaymentIcon from '@mui/icons-material/Payment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CampaignIcon from '@mui/icons-material/Campaign';
import { UniqCampLogo } from '@uniqcamp/shared';
import MapIcon from '@mui/icons-material/Map';
import LogoutIcon from '@mui/icons-material/Logout';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { authService, websocketService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { getFeatureAccessLookupKey } from '../utils/staffPermissionBlocks';
import { filterStaffWebDrawerNav, getStaffFeatureAccessRow } from '../utils/staffFeatureAccessPolicy';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import type { SchoolsResponse, DispersalsResponse } from '../types';

const DRAWER_WIDTH = 280;

const navItems = [
  { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/school-profile', label: 'School profile', icon: <SchoolIcon /> },
  { path: '/dispersals', label: 'Dispersals', icon: <SchoolIcon /> },
  { path: '/students', label: 'Students', icon: <PeopleIcon /> },
  { path: '/notifications', label: 'Notifications', icon: <NotificationsIcon /> },
  { path: '/meetings', label: 'Meetings', icon: <EventIcon /> },
];

const principalOnlyItems = [
  { path: '/staff', label: 'Staff', icon: <PeopleIcon /> },
  { path: '/classes', label: 'Classes', icon: <MenuBookIcon /> },
  { path: '/notices', label: 'Notices', icon: <CampaignIcon /> },
  { path: '/school', label: 'School', icon: <SchoolIcon /> },
  { path: '/gates', label: 'Gates', icon: <MapIcon /> },
  { path: '/staff-role-permissions', label: 'Staff Permissions', icon: <AdminPanelSettingsIcon /> },
  { path: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  { path: '/payments', label: 'Payments', icon: <PaymentIcon /> },
];

const pathToLabel: Record<string, string> = {
  '/': 'Dashboard',
  '/school-profile': 'School profile',
  '/dispersals': 'Dispersals',
  '/dispersals/early': 'Early dispersal',
  '/students': 'Students',
  '/students/relations': 'Relations',
  '/students/dispersal': 'Dispersal method',
  '/notifications': 'Notifications',
  '/meetings': 'Meetings',
  '/staff': 'Staff',
  '/staff/subjects': 'Subjects',
  '/staff/roles': 'Staff roles',
  '/staff/authority-levels': 'Authority levels',
  '/school': 'School',
  '/gates': 'Gates',
  '/staff-role-permissions': 'Staff Permissions',
  '/settings': 'Settings',
  '/settings/dispersal': 'Dispersal',
  '/classes': 'Classes',
  '/notices': 'Notices',
  '/payments': 'Payments',
};

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = authService.getStoredUser();
  const schoolReadOnly = Boolean((user as { schoolReadOnly?: boolean } | null)?.schoolReadOnly);

  // Real-time dispersal updates: connect WebSocket and invalidate dispersals list when backend emits
  const dispersalCallbackRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    websocketService.connect(token);
    const callback = () => queryClient.invalidateQueries('dispersals');
    dispersalCallbackRef.current = callback;
    websocketService.onDispersalUpdate(callback);
    return () => {
      if (dispersalCallbackRef.current) {
        websocketService.off('dispersal:update', dispersalCallbackRef.current);
      }
    };
  }, [user, queryClient]);
  const isPrincipal = user && canAccessPrincipalFeatures(user.role);
  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const r = await apiClient.get('/schools');
    return r.data as SchoolsResponse;
  }, { enabled: !!user });
  const school = schoolsData?.schools?.[0];
  const featureLookupKey = user ? getFeatureAccessLookupKey(user, school) : undefined;

  const today = new Date().toISOString().slice(0, 10);
  const { data: dispersalsData } = useQuery<DispersalsResponse>(
    ['dispersals', today],
    () => apiClient.get(`/dispersals?date=${today}`).then((r: { data: unknown }) => r.data as DispersalsResponse),
    { enabled: !!user }
  );
  const dispersals = dispersalsData?.dispersals ?? [];
  const pendingDispersalsCount = dispersals.filter(
    (d) => d.status === 'PENDING' || d.status === 'APPROVED_BY_TEACHER'
  ).length;

  const menuItems = useMemo(() => {
    const allItems = [...navItems, ...principalOnlyItems];
    if (isPrincipal) return allItems;
    const accessRow = getStaffFeatureAccessRow(school, featureLookupKey);
    return filterStaffWebDrawerNav(allItems, accessRow, false, !!featureLookupKey);
  }, [isPrincipal, featureLookupKey, school]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => authService.logout();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbLabels = pathSegments.length
    ? [{ label: 'Dashboard', path: '/' }, ...pathSegments.map((segment, i) => ({ label: pathToLabel['/' + pathSegments.slice(0, i + 1).join('/')] || segment, path: '/' + pathSegments.slice(0, i + 1).join('/') }))]
    : [{ label: 'Dashboard', path: '/' }];

  const drawer = (
    <Box sx={{ py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pb: 2 }}>
        {school?.logo ? (
          <>
            <Box
              component="img"
              src={school.logo}
              alt={school.name || 'School'}
              sx={{
                maxHeight: 48,
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            {!school.logo && school.name && (
              <Typography variant="body2" fontWeight={600} sx={{ mt: 1, lineHeight: 1.3 }}>
                {school.name}
              </Typography>
            )}
          
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
              <UniqCampLogo style={{ maxHeight: 40, width: 'auto' }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Staff Portal
            </Typography>
          </>
        )}
      </Box>
      <List sx={{ flex: 1, px: 1.5 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/'));
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  py: 1.25,
                  ...(isActive && {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.path === '/dispersals' && pendingDispersalsCount > 0 ? (
                    <Badge badgeContent={pendingDispersalsCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive ? 600 : 500 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton onClick={handleLogout} sx={{ py: 1.25 }} color="inherit">
            <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" fontWeight={500}>{String(user?.name ?? '')}</Typography>
        <Typography variant="caption" color="text.secondary">{String(user?.role ?? '')}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: DRAWER_WIDTH },
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 0, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {String(user?.name ?? '')}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" color="inherit">
            <NotificationsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              mt: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flex: 1, p: 0, display: 'flex', flexDirection: 'column', minWidth: 0, width: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        {schoolReadOnly ? (
          <Alert severity="warning" sx={{ borderRadius: 0, flexShrink: 0 }}>
            This school account is inactive. You can browse the portal, but changes are not allowed. Please contact your administrator.
          </Alert>
        ) : null}
        <Box sx={{ flex: 1, py: 3, px: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'auto' }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
              {breadcrumbLabels.map((b, i) =>
                i < breadcrumbLabels.length - 1 ? (
                  <Typography key={b.path} component="button" variant="body2" color="inherit" onClick={() => navigate(b.path)} sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                    {b.label}
                  </Typography>
                ) : (
                  <Typography key={b.path} variant="body2" color="text.primary" fontWeight={600}>
                    {b.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
            <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
